import { DisbursementOrdersQuery_disbursement } from './__generated__/DisbursementOrdersQuery';
import { Cell, FormulaCell, SheetCell, Sheet } from './types';
import { format } from 'date-fns-tz';
import { formatInTimeZone } from 'date-fns-tz';

export const isFormulaCell = (sheetCell: SheetCell): sheetCell is FormulaCell => {
  return (sheetCell as FormulaCell).formula !== undefined;
};

export const isNumberCell = (sheetCell: SheetCell): sheetCell is number => {
  return typeof sheetCell === 'number';
};

export const transformSheetCell = (sheetCell: SheetCell): Cell => {
  if (sheetCell === null) {
    return null;
  } else if (isFormulaCell(sheetCell)) {
    return {
      type: 'formula',
      value: sheetCell.formula,
    };
  } else if (isNumberCell(sheetCell)) {
    return {
      type: 'number',
      value: sheetCell,
    };
  } else {
    return {
      type: 'string',
      value: sheetCell,
    };
  }
};

export const transformSheetData = (sheet: Sheet): Cell[][] => {
  return sheet
    .filter((row) => row !== null)
    .map((row) => {
      return row.map(transformSheetCell);
    });
};

export type SortDirection = 'desc' | 'asc';

export const flipDirection = (direction: SortDirection): SortDirection => {
  return direction === 'desc' ? 'asc' : 'desc';
};

export const dateFormat = 'MMM. d, yyyy';
export const timeFormat = 'h:mm a';
export const fullDateFormat = dateFormat + ' ' + timeFormat;
const formatDisbursements = (disbursement) => format(new Date(disbursement.date), fullDateFormat);

export const getRowsForTips = (breakdown: any[] | null): Sheet | undefined => {
  if (breakdown) {
    const cells = breakdown.map((server) => {
      return [server.name, server.tip, server.sales];
    });

    return [[], ['Servers'], ['Name', 'Tips', 'Sales'], ...cells];
  } else {
    return [];
  }
};

export const getRowsForCategoryFromFinalReport = (category: any, startingRow: number): Sheet => {
  return [
    [category.name],
    ...category.items.map((item, index) => {
      const rowNumber = startingRow + index + 1;
      const optionPrices = item.options.reduce((total, option) => {
        return total + option.unitPrice;
      }, 0);

      return [
        item.name,
        item.unitPrice,
        item.options.length ? item.options.map((option) => option.name).join(', ') : null,
        optionPrices,
        item.quantity,
        {
          formula: `(B${rowNumber} + D${rowNumber}) * E${rowNumber}`,
        },
      ];
    }),
    [
      null,
      null,
      null,
      null,
      'Total:',
      {
        formula: `sum(F${startingRow + 1}:F${startingRow + category.items.length})`,
      },
    ],
    [],
  ];
};

export const getExcelDataForFinalReport = (session, venue): Cell[][] => {
  const timezone = venue.timezone;
  const isFreedomPay = venue.paymentProcessor === 'freedompay' || venue.paymentProcessor === 'square';

  const ordersForSession = session.orders;

  const itemRows = (): Sheet => {
    let startIndex;
    const allCategories = session.categories.map((category, index) => {
      startIndex = index === 0 ? 2 : startIndex + (session.categories[index - 1].items.length + 3);
      return getRowsForCategoryFromFinalReport(category, startIndex);
    });

    return allCategories.reduce((categoryRows, current) => {
      return categoryRows.concat(current);
    }, []);
  };

  const sheet: Sheet = [
    ['Item Name', 'Item Price', 'Options', 'Option Price', 'Quantity', 'Total'],
    ...itemRows(),
    session.fulfillmentCenter ? ['Fulfillment Center:', session.fulfillmentCenter.name] : null,
    [
      'Opened:',
      formatInTimeZone(new Date(session.opened), timezone, fullDateFormat),
      null,
      null,
      'Subtotal:',
      session.totalSubtotal,
    ],
    [
      'Closed:',
      session.closed ? formatInTimeZone(new Date(session.closed), timezone, fullDateFormat) : '',
      null,
      null,
      'Total Tax:',
      Math.round(session.totalTaxes * 100) / 100,
    ],
    [
      isFreedomPay ? null : 'Disbursement IDs',
      isFreedomPay ? null : session.disbursements.map((disbursement) => disbursement.id).join(','),
      null,
      null,
      'Total Tips:',
      session.totalTip,
    ],
    [
      isFreedomPay ? null : 'Disbursement Dates',
      isFreedomPay ? null : session.disbursements.map(formatDisbursements).join(', '),
      null,
      null,
      'Total Promos:',
      -1 * session.totalPromo,
    ],
    isFreedomPay ? [null, null, null, null, 'Total Noble Fees:', session.totalFees] : null,
    session.venueConvenienceFee > 0
      ? [null, null, null, null, 'Total Venue Convenience Fee:', session.venueConvenienceFee]
      : null,
    [null, null, null, null, 'Total Alcohol Tax:', session.totalAlcoholTax],
    [null, null, null, null, 'Total Food Tax:', session.totalFoodTax],
    [null, null, null, null, 'Total:', session.total + (isFreedomPay ? session.totalFees : 0)],
    [null, null, null, null, 'Total Refunded:', session.totalRefunded],
    [null, null, null, null, 'Net Total:', Math.round((session.total - session.totalRefunded) * 100) / 100],
    [],
    [],
    [
      'Order ID',
      'Cart',
      'Subtotal',
      'Total Tax',
      'Noble Fee',
      'Tip',
      'Order Total',
      'Refunded Amount',
      'Status',
      isFreedomPay ? null : 'Disbursed?',
      isFreedomPay ? null : 'Disbursement ID',
      isFreedomPay ? null : 'Disbursement Date',
    ],
    ...ordersForSession.map((order) => [
      order.id,
      order.cart
        .map((cartRow) => {
          const options = cartRow.options
            .map((option) => {
              return ` - ${option.name}`;
            })
            .join('\n');

          return `${cartRow.item.name} x ${cartRow.quantity}\n${options}`;
        })
        .join('\n'),
      order.subtotal,
      order.totalTax,
      order.nobleConvenienceFee,
      order.tip,
      order.total,
      order.refundedAmount,
      order.status ? order.status : 'Fulfilled',
      isFreedomPay ? null : order.disbursementId ? 'Yes' : 'No',
      isFreedomPay ? null : order.disbursementId || '',
      isFreedomPay ? null : order.date ? formatInTimeZone(new Date(order.date), timezone, 'MMM. d, yyyy') : '',
    ]),
    ...getRowsForTips(session.serverTipBreakdown),
  ];

  return transformSheetData(sheet);
};

export const getExcelForDisbursement = (result: DisbursementOrdersQuery_disbursement[]) => {
  return result.map((report: DisbursementOrdersQuery_disbursement) => {
    const data = [
      [
        'Order ID',
        'Date',
        'Session ID',
        'Session Opened',
        'Amount',
        'Tip',
        'Tax',
        'Service Fee',
        'Processing Fee',
        'Total',
      ].map((name) => ({
        type: 'string',
        value: name,
      })),
    ]
      .concat(
        report.rows.map((value) => {
          return [
            {
              type: 'number',
              value: value.orderId,
            },
            {
              type: 'string',
              value: format(new Date(value.createdAt), 'MMM. d, yyyy kk:mm'),
            },
            {
              type: 'number',
              value: value.sessionId,
            },
            {
              type: 'string',
              value: format(new Date(value.sessionOpened), 'MMM. d, yyyy kk:mm') as any,
            },
            {
              type: 'number',
              value: value.amount,
            },
            {
              type: 'number',
              value: value.tip,
            },
            {
              type: 'number',
              value: value.tax,
            },
            {
              type: 'number',
              value: value.serviceFee,
            },
            {
              type: 'number',
              value: value.processingFee,
            },
            {
              type: 'number',
              value: value.total,
            },
          ];
        })
      )
      .concat([
        [
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          {
            type: 'string',
            value: 'Total:',
          },
          {
            type: 'formula',
            value: `sum(J1:J${report.rows.length + 1})`,
          },
        ],
        [
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          {
            type: 'string',
            value: 'Processing Fee:',
          },
          {
            type: 'formula',
            value: `-1*sum(I1:I${report.rows.length + 1})`,
          },
        ],
        [
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          {
            type: 'string',
            value: 'Net:',
          },
          {
            type: 'formula',
            value: `sum(J${report.rows.length + 2}:J${report.rows.length + 3})`,
          },
        ],
      ]);

    return {
      name: format(new Date(report.rows[0].createdAt), 'MMM. d, yyyy'),
      data,
    };
  });
};
